import { APITypesV1, APIAssessment } from "@cur8/api-client";
import { AuditInformation } from "./AuditInformation";
import { Box } from "./Annotation";
import { DateTime } from "luxon";

export enum AssessmentTypeName {
  Base = "BaseAssessment",
  Pad = "PadAssessment",
  PulseWave = "PulseWaveAssessment",
}

type AbstractAssessment = {
  /** States representing the lifecycle of an assessment */
  assessmentState: APITypesV1.AssessmentState;
  /** Audit information */
  audit: AuditInformation;
  /** A free-text supplied by an operator */
  comment?: string | null;
  /** When the assessment version was created */
  createdAt: DateTime;
  /** ETag */
  eTag?: string | null;
  /** The full room ID including the site ID prefix */
  fullRoomId: string;
  /**
   * true for the document with ID {ItemTypeName}_{ItemId}_latest
   * true for the document with the same version as the _latest doc
   */
  isLatestVersion: boolean;
  /**
   * ID of the assessment
   * @format uuid
   */
  itemId: string;
  /**
   * Version of the assessment
   * @format uuid
   */
  itemVersion: string;
  /** Type name */
  itemTypeName: AssessmentTypeName;
  /** Patient Group IDs that the assessment is associated with */
  patientGroupIds: string[];
  /** ExternalID of the patient */
  patientId: string;
  /** Represents a DTO summary of a patient denormalized */
  patientSummary?: APITypesV1.PatientSummary;
  /**
   * The ID of the Scan that the assessment is associated with
   * @format uuid
   */
  scanId: string;
  /**
   * The version of the Scan that the assessment is associated with
   * @format uuid
   */
  scanVersion: string;
  /** ID of the site where the visit took place */
  siteId: string;
  /**
   * The ID of the visit that the assessment is associated with
   * @format uuid
   */
  visitId: string;
};

export interface RegionsOfInterestBox {
  /** We use Box on the client side (the API uses Rect) */
  forearm: Box;
  hypothenar: Box;
}

export type PadAssessmentBox = AbstractAssessment & {
  /** Ratios */
  ratios?: APITypesV1.PadAssessmentRatios;
  /** Represents regions of interest. */
  regionsOfInterest?: RegionsOfInterestBox;
};

export type PulseWaveAssessment = AbstractAssessment & {
  /** Represents the type of Pulse Wave summary. */
  lveti?: APITypesV1.PulseWaveAssessmentData;
  /** Represents the type of Pulse Wave summary. */
  pulseWaveVelocity?: APITypesV1.PulseWaveAssessmentData;
  /** Represents the type of Pulse Wave summary. */
  handAsymmetry?: APITypesV1.PulseWaveAssessmentData;
  /** Represents the type of Pulse Wave summary. */
  footAsymmetry?: APITypesV1.PulseWaveAssessmentData;
};

export type Assessment = PadAssessmentBox | PulseWaveAssessment;

/**
 * Note: Should these type guards be somewhere else?
 */
export function isPadAssessment(src: Assessment): src is PadAssessmentBox {
  return src.itemTypeName === AssessmentTypeName.Pad;
}
export function isAPIPadAssessment(
  src: APIAssessment
): src is APITypesV1.PadAssessment {
  if (
    src.hasOwnProperty("ratios") ||
    src.hasOwnProperty("regionsOfInterest") ||
    (src.itemTypeName && src.itemTypeName === "PadAssessment")
  ) {
    return true;
  }
  return false;
}

export function isPulseWaveAssessment(
  src: Assessment
): src is PulseWaveAssessment {
  return src.itemTypeName === AssessmentTypeName.PulseWave;
}
export function isAPIPulseWaveAssessment(
  src: APIAssessment
): src is APITypesV1.PulseWaveAssessment {
  if (
    src.hasOwnProperty("lveti") ||
    src.hasOwnProperty("pulseWaveVelocity") ||
    src.hasOwnProperty("handAsymmetry") ||
    src.hasOwnProperty("footAsymmetry") ||
    (src.itemTypeName && src.itemTypeName === "PulseWaveAssessment")
  ) {
    return true;
  }
  return false;
}
